import { getShipDefinition } from "./ships.js";
import { RESOURCE_NAMES } from "./resources.js";
/**
 * Flight-system constants from
 * `docs/game-details/flight_management_system.md` (§13-§18, §26 formula
 * summary). The formulas themselves live in
 * `packages/game-engine/src/flightEngine.ts` — this file only holds the
 * tunable numbers so client display code and the server never re-declare
 * them.
 *
 * Deliberately absent: speed/scrap motivation and admiral bonuses (§11-§12,
 * §15). Those are premium-credit / admiral features with no underlying
 * system in Kaeliux yet; the time formula accepts a neutral
 * `speedMotivationPercent` parameter so they can land later without a
 * formula version bump.
 */
// ---------------------------------------------------------------------------
// Distance (§13)
// ---------------------------------------------------------------------------
/** `d = |Δgalaxy| × 9000` when galaxies differ. */
export const FLIGHT_GALAXY_DISTANCE_FACTOR = 9000;
/** `d = 1300 + 95 × |Δsystem|` when systems differ within a galaxy. */
export const FLIGHT_SYSTEM_DISTANCE_BASE = 1300;
export const FLIGHT_SYSTEM_DISTANCE_FACTOR = 95;
/** `d = 1000 + 5 × |Δposition|` when only the orbit position differs. */
export const FLIGHT_POSITION_DISTANCE_BASE = 1000;
export const FLIGHT_POSITION_DISTANCE_FACTOR = 5;
/** Displayed distance in AU: `d × 1000`. */
export const FLIGHT_DISTANCE_AU_FACTOR = 1000;
// ---------------------------------------------------------------------------
// Time (§14-§16)
// ---------------------------------------------------------------------------
/** Numerator of the base-time formula: `round(35000 / p × sqrt(d × 10 / fleetSpeed)) + 10`. */
export const FLIGHT_TIME_FACTOR = 35000;
/** Flat seconds added to every computed base time. */
export const FLIGHT_TIME_BASE_SECONDS = 10;
/** `FLEETS_MIN_TIME` — floor applied after all modifiers. */
export const FLEETS_MIN_TIME_SECONDS = 10;
/** Selectable travel speeds, percent of full fleet speed (§12). */
export const FLIGHT_SPEED_PERCENT_OPTIONS = [100, 90, 80, 70, 60, 50, 40, 30, 20, 10];
/** Same-coordinate base seconds at 100% speed (§16): asteroid 20 min, recycle 15 min. */
export const SAME_COORDINATE_MISSION_BASE_SECONDS = {
    harvestAsteroid: 20 * 60,
    recycle: 15 * 60,
};
/** Recycle targeting a Sun coordinate (`position = 0`): 600 min at 100% speed (§16). */
export const RECYCLE_SUN_BASE_SECONDS = 600 * 60;
/** Space Trip manual duration bounds (0-24 h / 0-59 min dropdowns; gameplay ruling, 2026-07-20). */
export const SPACE_TRIP_MAX_HOURS = 24;
export const SPACE_TRIP_MAX_MINUTES = 59;
/** Minimum Space Trip duration: 5 minutes (gameplay ruling, 2026-07-20). */
export const SPACE_TRIP_MIN_SECONDS = 5 * 60;
/** Maximum Space Trip duration: 24 hours one-way, 48 hour round trip (gameplay ruling, 2026-07-20). */
export const SPACE_TRIP_MAX_SECONDS = 24 * 3600;
/**
 * How long Espionage fleets sit at the target before automatically heading
 * home — a fixed real-world duration, unaffected by the universe flight
 * multiplier (same precedent as Space Trip's manual duration). Recall works
 * during this window too (§3.1 exception).
 */
export const ESPIONAGE_LOITER_SECONDS = 24 * 3600;
/**
 * Sun Support's return-transit duration, once triggered. UNLIKE Espionage,
 * this is NOT a fixed loiter-then-auto-return timer from arrival — a Sun
 * Support fleet registers as a defender and stays registered INDEFINITELY
 * (no scheduled `returnsAt`) until the Sun's pending attack either resolves
 * in a real battle or is recalled by the attacker; only THEN does every
 * currently-registered defender start this flat 24h trip home. See
 * `apps/functions/src/suns.ts` — the far-future placeholder `returnsAt` a
 * defender arrives with, and how a real one gets stamped later.
 */
export const SUN_DEFENCE_RETURN_HOLD_SECONDS = 24 * 3600;
/**
 * Dispatch-time placeholder for a Sun Support fleet's `returnsAt` — "never,
 * until something rewrites it" (100 years out). `fleetDispatch.ts` stamps
 * this instead of `arrivesAt + loiterSeconds` specifically for `sunDefence`,
 * since that mission's `loiterSeconds` field exists only to mark it as a
 * loitering mission for {@link SUN_DEFENCE_RETURN_HOLD_SECONDS}'s OWN
 * purpose (the real return-transit duration once triggered), not as a fixed
 * auto-return timer. Two things ever replace this placeholder with a real
 * `returnsAt`: a player recall (`recallFleet`, fleetResolution.ts) or the
 * Sun's pending attack actually resolving in battle
 * (`apps/functions/src/suns.ts`) — both stamp exactly `now + SUN_DEFENCE_RETURN_HOLD_SECONDS`.
 */
export const SUN_DEFENCE_INDEFINITE_PLACEHOLDER_SECONDS = 100 * 365 * 24 * 3600;
/**
 * Sun Support population limits (gameplay ruling, 2026-07-20; revised
 * 2026-08-14 into real per-player/total caps once Sun battles became real —
 * see `SUN_DEFENSE_PER_PLAYER_POPULATION_CAP`/`SUN_DEFENSE_TOTAL_POPULATION_CAP`
 * below). `SUN_SUPPORT_MAX_POPULATION` is this fleet's OWN cap, distinct
 * from the total-across-all-defenders cap.
 */
export const SUN_SUPPORT_MIN_POPULATION = 2000;
export const SUN_SUPPORT_MAX_POPULATION = 261900;
/** Ship ID 3 (Vanguard / Rivet / Mite) is capped within a Sun Support fleet specifically. */
export const SUN_SUPPORT_SHIP3_MAX_POPULATION = 500;
/**
 * Cross-player Sun-defense caps (2026-08-14 gameplay ruling), enforced
 * against the Sun's CURRENT total registered-defender population, not just
 * one fleet's own manifest:
 *   - No single player may have more than this much population registered
 *     at one Sun at once (summed across all of that player's OWN currently
 *     registered defending fleets there).
 *   - The Sun's defenders may never total more than this, across every
 *     player combined.
 */
export const SUN_DEFENSE_PER_PLAYER_POPULATION_CAP = 60000;
export const SUN_DEFENSE_TOTAL_POPULATION_CAP = 269100;
/** Minimum total population a `sunAttack` fleet must carry (2026-08-14 gameplay ruling). No upper bound is defined. */
export const SUN_ATTACK_MIN_POPULATION = 100000;
/**
 * After a Sun Attack fleet's battle resolves at a Sun, no NEW attack may be
 * dispatched there for this long — enforced even when no attacker is
 * currently claiming the Sun (a separate, longer-lived cooldown from "is
 * someone already inbound"). A recalled attack that never reached battle
 * does NOT stamp this cooldown (2026-08-14 gameplay ruling).
 */
export const SUN_ATTACK_COOLDOWN_HOURS = 48;
/**
 * Fraction of a Sun Support fleet's DESTROYED units that return home anyway
 * (half its losses are recoverable, half are permanent) — applies
 * regardless of the battle's outcome. Fraction of the Sun Attack fleet's
 * destroyed units that return, ONLY when the attack wins outright
 * (2026-08-14 gameplay ruling — a heavy defender incentive: attackers get
 * less back, and only on a clean win).
 */
export const SUN_DEFENDER_LOSS_RETURN_RATE = 0.5;
export const SUN_ATTACKER_LOSS_RETURN_RATE_ON_WIN = 0.3;
/** Sun-battle defenders earn battle points at this multiple of the normal Attack/Pirate formula (2026-08-14 gameplay ruling — a heavy incentive to participate in defence). */
export const SUN_DEFENDER_BATTLE_POINTS_MULTIPLIER = 3;
/** Pirate fleets always fight exactly this many battle rounds — not player-selectable. */
export const PIRATE_FIXED_BATTLE_ROUNDS = 3;
// ---------------------------------------------------------------------------
// Gas (§18)
// ---------------------------------------------------------------------------
/** Divisor in `gas = round(fuel × d / 35000 × ((p/10)+1)²) + 1`. */
export const FLIGHT_GAS_DISTANCE_DIVISOR = 35000;
/** Space Trip has no distance — flat gas cost before the Chemistry reduction (§20). */
export const SPACE_TRIP_GAS_COST = 100;
// ---------------------------------------------------------------------------
// Flight limits (§2)
// ---------------------------------------------------------------------------
/** Base allowance in `max = ceil(1 + 1.5 × signalSystems + pilotSkill)`. */
export const FLIGHT_LIMIT_BASE = 1;
export const FLIGHT_LIMIT_PER_RADAR_LEVEL = 1.5;
/** Espionage fleets get their own separate pool: 5× the normal limit (§2.1). */
export const ESPIONAGE_FLIGHT_LIMIT_MULTIPLIER = 5;
// ---------------------------------------------------------------------------
// Battle rounds (§10)
// ---------------------------------------------------------------------------
/** Player-selectable round counts for the `attack` mission (§10). */
export const BATTLE_ROUND_OPTIONS = [5, 8];
export const DEFAULT_BATTLE_ROUNDS = 8;
/**
 * Forced (non-selectable) round count for `sunAttack`. Not part of
 * {@link BATTLE_ROUND_OPTIONS} — a Sun Attack fleet never picks its own
 * round count, same as Pirate's `PIRATE_FIXED_BATTLE_ROUNDS`.
 *
 * "Attacking another team's planet" (the old design's second use of this
 * constant) remains unreachable: `attack` is not extended to Team planet
 * targets in this iteration — a Team planet's only inbound missions are
 * Transport/Station, matching `docs/game-details/flight_management_system.md`
 * §9: "Team planets can only interact with a Sun or other Team Planets."
 */
export const TEAM_BATTLE_ROUNDS = 12;
export const FLEET_MISSION_DEFINITIONS = {
    transport: {
        id: "transport",
        name: "Transport",
        description: "Move resources to one of your own planets. Ships return after delivery.",
        allowsCargo: true,
        requiresCargo: true,
        allowsBattleRounds: false,
        // Own planets, and (once founded) the caller's own alliance's team planet — flight doc §8.1.
        ownPlanetTargeting: "required",
        allowsAllianceTeamPlanetTarget: true,
        hasReturnLeg: true,
        logCategory: "general",
    },
    espionage: {
        id: "espionage",
        name: "Espionage",
        description: "Get intel on an enemy planet — resources, population and fleet. May also target another alliance's Team planet, or a Sun to learn which alliance controls it.",
        allowsCargo: false,
        allowsBattleRounds: false,
        ownPlanetTargeting: "forbidden",
        allowsAnyTeamPlanetTarget: true,
        // A concealed Sun's controller is revealed when the sender's
        // Intelligence beats the controlling Team planet's (sunControl.ts).
        allowsSunTarget: true,
        onlyShipIds: [12],
        requiredShipId: 12,
        maxTotalShips: 1,
        loiterSeconds: ESPIONAGE_LOITER_SECONDS,
        lockedAfterGuestUpgrade: true,
        hasReturnLeg: true,
        // Arrival intel lives fully in SpyReport, not a FlightLog (see
        // flightLog.types.ts) — the return leg still logs as 'general'.
        logCategory: "general",
    },
    attack: {
        id: "attack",
        name: "Attack",
        description: "Attack a planet to destroy ships and defences and steal resources.",
        allowsCargo: true,
        allowsBattleRounds: true,
        ownPlanetTargeting: "allowed",
        lockedAfterGuestUpgrade: true,
        hasReturnLeg: true,
        logCategory: "battleReport",
    },
    station: {
        id: "station",
        name: "Station",
        description: "Move ships permanently to another of your planets.",
        allowsCargo: true,
        allowsBattleRounds: false,
        ownPlanetTargeting: "required",
        allowsAllianceTeamPlanetTarget: true,
        hasReturnLeg: false,
        logCategory: "general",
    },
    colonize: {
        id: "colonize",
        name: "Colonize",
        description: "Found a new colony at empty coordinates. Consumes the {ship:10}.",
        allowsCargo: true,
        allowsBattleRounds: false,
        ownPlanetTargeting: "forbidden",
        requiresEmptyTarget: true,
        requiredShipId: 10,
        onlyShipIds: [10],
        maxTotalShips: 1,
        hasReturnLeg: true,
        logCategory: "general",
    },
    recycle: {
        id: "recycle",
        name: "Recycle",
        description: `Collect ${RESOURCE_NAMES.scrap} from a location. Sun coordinates can hold large ${RESOURCE_NAMES.scrap} fields.`,
        allowsCargo: false,
        allowsBattleRounds: false,
        ownPlanetTargeting: "allowed",
        allowsEmptyTarget: true,
        allowsSunTarget: true,
        requiredShipId: 11,
        onlyShipIds: [11],
        hasReturnLeg: true,
        logCategory: "general",
    },
    pirate: {
        id: "pirate",
        name: "Pirate",
        description: "Steal resources using {ship:3}s. Always fights a fixed 3 combat rounds.",
        allowsCargo: true,
        allowsBattleRounds: false,
        fixedBattleRounds: PIRATE_FIXED_BATTLE_ROUNDS,
        ownPlanetTargeting: "forbidden",
        requiredShipId: 3,
        onlyShipIds: [3],
        defenderOnlyShipIds: [3],
        lockedAfterGuestUpgrade: true,
        hasReturnLeg: true,
        logCategory: "battleReport",
        // TODO: limit pirate raids to 3 per day per target planet
    },
    harvestAsteroid: {
        id: "harvestAsteroid",
        name: "Asteroid mining",
        description: `Collect resources, ${RESOURCE_NAMES.gas} and ${RESOURCE_NAMES.scrap} from a passing asteroid.`,
        allowsCargo: false,
        allowsBattleRounds: false,
        ownPlanetTargeting: "allowed",
        allowsEmptyTarget: true,
        requiredShipId: 11,
        onlyShipIds: [11],
        hasReturnLeg: true,
        logCategory: "general",
    },
    sunDefence: {
        id: "sunDefence",
        name: "Sun Support",
        description: "Register as a defender at an uncontrolled Sun under attack. Stays until the attack resolves or is recalled, then takes 24h to return. Individual players only — never a Team planet, and never at a Sun an alliance controls.",
        allowsCargo: false,
        allowsBattleRounds: false,
        ownPlanetTargeting: "forbidden",
        forcesSunTarget: true,
        onlyShipCategory: "war",
        requiresSunUnderAttack: true,
        loiterSeconds: SUN_DEFENCE_RETURN_HOLD_SECONDS,
        lockedAfterGuestUpgrade: true,
        hasReturnLeg: true,
        logCategory: "battleReport",
    },
    sunAttack: {
        id: "sunAttack",
        name: "Sun Attack",
        description: "Attack a Sun in one massive battle, always over the reserved 12 battle rounds — against every registered defender at an uncontrolled Sun, or against the garrison at a Sun another alliance controls. A win claims the Sun: the survivors become its garrison. At most one inbound per alliance at a time, and a 48h cooldown per Sun after a battle. Only dispatchable from your alliance's Team planet.",
        allowsCargo: false,
        allowsBattleRounds: false,
        fixedBattleRounds: TEAM_BATTLE_ROUNDS,
        ownPlanetTargeting: "forbidden",
        forcesSunTarget: true,
        onlyShipCategory: "war",
        teamPlanetOrigin: "only",
        lockedAfterGuestUpgrade: true,
        hasReturnLeg: true,
        logCategory: "battleReport",
    },
    sunGarrison: {
        id: "sunGarrison",
        name: "Sun Garrison",
        description: "Station war ships at a Sun your alliance has claimed or controls. They join its garrison, which holds the Sun and fights any reclaim, and leave the Team planet's population.",
        allowsCargo: false,
        allowsBattleRounds: false,
        ownPlanetTargeting: "forbidden",
        forcesSunTarget: true,
        onlyShipCategory: "war",
        teamPlanetOrigin: "only",
        // Garrisoned ships stay at the Sun; only a rejected or overflowing
        // landing turns them back, on the return leg planned at dispatch.
        hasReturnLeg: false,
        logCategory: "general",
    },
    spaceTrip: {
        id: "spaceTrip",
        name: "Space Trip",
        description: "Send ships (and optionally resources) away temporarily so they cannot be attacked. Duration is picked manually.",
        // Cargo IS allowed (gameplay ruling, 2026-07-20 — overrides the flight
        // doc's "produced resources remain on the planet", which describes
        // what happens to resources NOT loaded) — INCLUDING from a Team
        // planet (issue #158): it comes home to the same planet it left, so
        // this is fleet-saving the alliance's own stockpile, not a way to
        // move it out. `TeamPlanetFleetsPage` shows the Team planet's
        // current balance next to the cargo picker precisely so a member
        // loading it can see what they're moving.
        allowsCargo: true,
        allowsBattleRounds: false,
        ownPlanetTargeting: "allowed",
        manualDuration: true,
        teamPlanetOrigin: "allowed",
        allowedForGuests: true,
        hasReturnLeg: true,
        logCategory: "general",
    },
};
/**
 * A mission's description with each `{ship:N}` token replaced by the name of
 * `race`'s ship ID N, so text that names a ship reads correctly for every race.
 */
export function getMissionDescription(mission, race) {
    return FLEET_MISSION_DEFINITIONS[mission].description.replace(/\{ship:(\d+)\}/g, (_, id) => getShipDefinition(race, Number(id)).name);
}
export const ALL_FLEET_MISSIONS = Object.keys(FLEET_MISSION_DEFINITIONS);
/**
 * Missions dispatchable from a normal (non-Team) planet — every mission
 * except Sun Attack and Sun Garrison, which are Team-planet-origin only. Derived from each
 * mission's own {@link FleetMissionDefinition.teamPlanetOrigin} rather than
 * a hand-maintained list, so a mission's origin rule only ever needs to be
 * declared once.
 */
export const PLAYER_PLANET_MISSIONS = ALL_FLEET_MISSIONS.filter((mission) => FLEET_MISSION_DEFINITIONS[mission].teamPlanetOrigin !== "only");
/**
 * Missions dispatchable from an alliance's Team planet — Sun Attack, Sun
 * Garrison and Space Trip only (issue #158). Same derivation as
 * {@link PLAYER_PLANET_MISSIONS}.
 */
export const TEAM_PLANET_MISSIONS = ALL_FLEET_MISSIONS.filter((mission) => FLEET_MISSION_DEFINITIONS[mission].teamPlanetOrigin !== undefined);
/**
 * Ships restricted to flying ONLY these missions, regardless of what a
 * mission's own `onlyShipIds`/`onlyShipCategory` would otherwise permit —
 * nothing stops a spy ship from joining a Transport manifest unless it's
 * listed here. Race-agnostic: every race's ship sharing an ID has the same
 * role (e.g. Lurker / Monitor / Synapse are all ID 12).
 */
export const SHIP_MISSION_RESTRICTIONS = {
    12: ["espionage", "spaceTrip", "station"], // Lurker / Monitor / Synapse
};
/** Orbital power plants (ID 13) have speed 0 and can never join a fleet. */
export const UNFLYABLE_SHIP_IDS = [13];
//# sourceMappingURL=fleets.js.map